import { useEffect, useState } from "react";
import { api, API_BASE, getToken } from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { FileText, Wallet } from "lucide-react";
import HelpVideoButton from "@/components/HelpVideoButton";

export default function HelperReports() {
  const { t } = useT();
  const [cash, setCash] = useState([]);
  const [passes, setPasses] = useState([]);

  useEffect(() => {
    api.get("/helper/cash").then((r) => setCash(r.data));
    api.get("/helper/gatepasses").then((r) => setPasses(r.data));
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const todayCash = cash.filter((x) => (x.created_at || "").startsWith(today));
  const totalToday = todayCash.reduce((s, x) => s + (x.amount || 0), 0);
  const totalAll = cash.reduce((s, x) => s + (x.amount || 0), 0);

  const dl = (url, name) =>
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = name;
        a.click();
      });

  return (
    <DashboardShell title={t("nav.reports")} subtitle="Your recent receipts and gate passes" action={<HelpVideoButton screen="reports" />}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="am-card p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Today collected</div>
          <div className="font-display text-3xl font-extrabold mt-2">₹ {totalToday.toLocaleString("en-IN")}</div>
          <div className="text-xs text-muted-foreground mt-1">{todayCash.length} receipts</div>
        </div>
        <div className="am-card p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">All-time collected</div>
          <div className="font-display text-3xl font-extrabold mt-2">₹ {totalAll.toLocaleString("en-IN")}</div>
          <div className="text-xs text-muted-foreground mt-1">{cash.length} receipts</div>
        </div>
        <div className="am-card p-6 bg-accent border-accent">
          <div className="text-xs uppercase tracking-widest font-bold text-accent-foreground">Gate passes issued</div>
          <div className="font-display text-3xl font-extrabold mt-2 text-accent-foreground">{passes.length}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="am-card p-2 overflow-x-auto">
          <h2 className="font-display text-lg font-bold p-4 pb-2 flex items-center gap-2">
            <Wallet className="w-4 h-4 text-primary" /> Recent receipts
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
                <th className="px-4 py-3">Receipt</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {cash.slice(0, 15).map((c) => (
                <tr key={c.collection_id} className="border-b last:border-0">
                  <td className="px-4 py-3 font-mono text-xs">{c.receipt_number}</td>
                  <td className="px-4 py-3 font-mono font-bold">₹ {Number(c.amount).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => dl(`${API_BASE}/helper/cash/${c.collection_id}/pdf`, `${c.receipt_number}.pdf`)}>
                      PDF
                    </Button>
                  </td>
                </tr>
              ))}
              {cash.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-muted-foreground">No receipts yet.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="am-card p-2 overflow-x-auto">
          <h2 className="font-display text-lg font-bold p-4 pb-2 flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" /> Recent gate passes
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
                <th className="px-4 py-3">Pass</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {passes.slice(0, 15).map((g) => (
                <tr key={g.gatepass_id} className="border-b last:border-0">
                  <td className="px-4 py-3 font-mono text-xs">{g.pass_number}</td>
                  <td className="px-4 py-3 capitalize"><span className="am-chip">{g.pass_type}</span></td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => dl(`${API_BASE}/helper/gatepasses/${g.gatepass_id}/pdf`, `${g.pass_number}.pdf`)}>
                      PDF
                    </Button>
                  </td>
                </tr>
              ))}
              {passes.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-muted-foreground">No passes yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardShell>
  );
}
